import type { ComponentType, SVGProps } from 'react';
import { ExclamationTriangleIcon, UserMinusIcon } from '@heroicons/react/24/outline';

function Bar({ className }: { className: string }) {
    return <div className={`rounded bg-gray-200 animate-pulse ${className}`} />;
}

const PANEL_CLASS = 'rounded-xl border border-gray-200 bg-white';

export function PatientViewSkeleton() {
    return (
        <div className="flex h-full min-h-0 flex-col gap-3 px-8 pb-4" aria-busy="true">
            <div className={`${PANEL_CLASS} flex items-center gap-3 px-4 py-3`}>
                <Bar className="h-8 w-8 rounded-lg" />
                <Bar className="h-10 w-10 rounded-full" />
                <div className="flex flex-col gap-1.5">
                    <Bar className="h-3.5 w-40" />
                    <Bar className="h-3 w-28" />
                </div>
                <Bar className="ml-auto h-8 w-40 rounded-lg" />
            </div>
            <div className="flex min-h-0 flex-1 gap-3">
                <div className={`${PANEL_CLASS} flex flex-[3] flex-col gap-2 p-3`}>
                    <Bar className="h-28 rounded-lg" />
                    <Bar className="h-28 rounded-lg" />
                </div>
                <div className={`${PANEL_CLASS} flex-[7] p-3`}>
                    <Bar className="h-full rounded-lg" />
                </div>
            </div>
        </div>
    );
}

interface MessageAction {
    label: string;
    onClick: () => void;
    primary?: boolean;
}

interface ViewMessageProps {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    title: string;
    text: string;
    actions: MessageAction[];
}

function ViewMessage({ icon: Icon, title, text, actions }: ViewMessageProps) {
    return (
        <div className="flex h-full min-h-0 flex-col px-8 pb-4">
            <div
                className={`${PANEL_CLASS} flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center`}
            >
                <div className="grid h-10 w-10 place-items-center rounded-full border border-gray-200 bg-white text-gray-400">
                    <Icon aria-hidden="true" className="h-[18px] w-[18px]" />
                </div>
                <h2 className="text-[13.5px] font-semibold text-gray-800">{title}</h2>
                <p className="max-w-xs text-[12.5px] leading-relaxed text-gray-500">{text}</p>
                <div className="mt-1 flex items-center gap-2">
                    {actions.map((action) => (
                        <button
                            key={action.label}
                            type="button"
                            onClick={action.onClick}
                            className={
                                action.primary
                                    ? 'rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-gray-800'
                                    : 'rounded-lg border border-gray-300 bg-white px-4 py-2 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50'
                            }
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

const DEFAULT_BACK_LABEL = 'Back to list';

interface PatientViewNotFoundProps {
    onBackToList: () => void;
    backLabel?: string;
}

export function PatientViewNotFound({
    onBackToList,
    backLabel = DEFAULT_BACK_LABEL
}: PatientViewNotFoundProps) {
    return (
        <ViewMessage
            icon={UserMinusIcon}
            title="Patient not found"
            text="This patient doesn't exist or is no longer available."
            actions={[{ label: backLabel, onClick: onBackToList, primary: true }]}
        />
    );
}

interface PatientViewErrorProps {
    // What failed to load, e.g. "this patient's notifications".
    subject: string;
    onRetry: () => void;
    onBackToList: () => void;
    backLabel?: string;
}

export function PatientViewError({
    subject,
    onRetry,
    onBackToList,
    backLabel = DEFAULT_BACK_LABEL
}: PatientViewErrorProps) {
    return (
        <ViewMessage
            icon={ExclamationTriangleIcon}
            title={`Couldn't load ${subject}`}
            text="Something went wrong fetching them. Try again."
            actions={[
                { label: 'Retry', onClick: onRetry, primary: true },
                { label: backLabel, onClick: onBackToList }
            ]}
        />
    );
}
